import React, { useMemo } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';

import { TableView } from '~components/table-view';

import pricePlanColumns from '~support-at-home/forms/sah-associations/cutoverPricePlans';

import { CutoverContext, useGetCutoverPricePlansQuery } from '~support-at-home/actions/sahServiceCutover';

import { usePublishedValue, useSeededSelection } from './hooks/useSelectionSync';
import StepPanel from './StepPanel';

interface ComponentProps {
  value: CutoverContext & { service_type_ids?: number[]; plan_ids?: string[] };
  onChange?: (name: string, value: any) => any;
}

/**
 * Step 3 - the price plans carrying these services across the cutover date.
 *
 * Each chosen plan gets a new revision carrying the replacement, priced from the service being
 * replaced. The prices are copied as they stand: the assistant never adjusts one, because a
 * different price is set by editing the new revision afterwards, exactly as any other price is. The
 * step says so before the provider confirms rather than after, so nobody expects it to do pricing.
 *
 * A plan whose latest revision already carries the replacement is shown as already carried and is
 * not given a second, identical revision.
 */
const PricePlanStep: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { cutover_date, pairs = [], service_type_ids = [] } = value;

  const isSkipped = !cutover_date || pairs.length === 0 || service_type_ids.length === 0;

  const { data, isFetching, error } = useGetCutoverPricePlansQuery(
    { cutover_date, pairs, service_type_ids },
    { skip: isSkipped, refetchOnMountOrArgChange: true },
  );

  const rows = useMemo(() => (data?.rows ?? []).map((row) => ({ ...row, id: row.plan_id })), [data]);

  // No plan to carry at all, so there is nothing to confirm. Only once the query has settled: RTK
  // keeps the previous `data` through a refetch and through an error, and a stale empty answer must
  // not turn the button into a plain Next while plans are on their way.
  const nothingToCarry = isSkipped || (data !== undefined && !isFetching && !error && rows.length === 0);

  // Plans still needing the replacement are selected by default; ones already carrying it are not,
  // so confirming without touching anything does exactly the work that is outstanding.
  const [selectedIds, handleSelection] = useSeededSelection(
    rows,
    data !== undefined,
    (allRows) => allRows.filter(({ already_carried }) => !already_carried).map(({ id }) => id),
    (ids) => onChange?.('plan_ids', ids),
  );

  // Selected AND not already carrying the replacement - the plans that will actually get a new
  // revision. Published to the form so the confirmation dialog quotes the same number.
  const pending = rows.filter(({ id, already_carried }) => selectedIds.includes(id) && !already_carried).length;

  usePublishedValue(pending, (next) => onChange?.('price_plans_pending', next));

  // Published so the wizard can offer a plain Next, with no confirmation, when there is nothing to
  // confirm.
  usePublishedValue(nothingToCarry, (next) => onChange?.('price_plans_empty', next));

  return (
    <StepPanel
      id="cutover-price-plans"
      header={`${rows.length} price plan${rows.length === 1 ? '' : 's'} in effect across the cutover`}
      isFetching={isFetching}
      error={error}
      errorMessage={error ? getErrorMessage(error) : undefined}
      isComplete={Boolean(data?.is_complete)}
      hasRows={rows.length > 0}
      emptyMessage={`No price plan in effect across the cutover date carries these services for the selected service types.${
        nothingToCarry ? ' Select Next to continue.' : ''
      }`}
      completeMessage="Every one of these plans already carries the replacement on its latest revision. This step is already done - continue to the next one."
      outcome={[
        `Add a new revision to ${pending} price plan${pending === 1 ? '' : 's'}, carrying the replacement service alongside everything the current revision holds.`,
        'Prices are copied from the services being replaced exactly as they stand. To charge a different price, edit the new revision afterwards as you would any other.',
        'Stopping after this step leaves the new revisions in place. Existing budgets stay on the revision they are on until the budget step is confirmed.',
      ]}
      warning="Confirming to the next step will add a new revision to these price plans, please ensure you have verified your selection."
    >
      <TableView
        id="cutover-price-plans-table"
        data={rows}
        columns={pricePlanColumns}
        readonly
        striped
        canSelect
        selectedIds={selectedIds}
        onRowSelectionChange={handleSelection}
        showAddRow={false}
      />
    </StepPanel>
  );
};

export default PricePlanStep;
